import React, { useEffect, useMemo, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import { getPlanLogRevenueChart } from "../../../api/plan-log";

const FILTERS = [
	{ id: "day", label: "Día", hint: "Últimos 30 días" },
	{ id: "week", label: "Semana", hint: "Últimas 12 semanas" },
	{ id: "month", label: "Mes", hint: "Últimos 12 meses" },
	{ id: "year", label: "Año", hint: "Últimos 5 años" },
];

function formatEurFromCents(cents) {
	const n = Number(cents);
	if (!Number.isFinite(n)) return "—";
	return new Intl.NumberFormat("es-ES", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(n / 100);
}

function buildChartOptions({ labels, seriesCents, granularity }) {
	const euros = seriesCents.map((c) => Math.round(Number(c) || 0) / 100);
	const peak = Math.max(...euros, 0);

	return {
		chart: {
			type: "bar",
			height: 340,
			fontFamily: "Poppins, Helvetica, sans-serif",
			foreColor: "#7e8299",
			toolbar: { show: false },
			zoom: { enabled: false },
			animations: {
				enabled: true,
				easing: "easeinout",
				speed: 900,
				animateGradually: { enabled: true, delay: 40 },
				dynamicAnimation: { enabled: true, speed: 450 },
			},
		},
		plotOptions: {
			bar: {
				borderRadius: 8,
				columnWidth: granularity === "day" ? "62%" : "48%",
				dataLabels: { position: "top" },
			},
		},
		colors: ["#24284f"],
		fill: {
			type: "gradient",
			gradient: {
				shade: "light",
				type: "vertical",
				shadeIntensity: 0.35,
				gradientToColors: ["#5d6bff"],
				inverseColors: false,
				opacityFrom: 1,
				opacityTo: 0.82,
				stops: [0, 100],
			},
		},
		series: [{ name: "Ingresos", data: euros }],
		dataLabels: { enabled: false },
		stroke: { show: true, width: 2, colors: ["transparent"] },
		grid: {
			borderColor: "#ebedf3",
			strokeDashArray: 4,
			padding: { top: 12, right: 16, bottom: 0, left: 12 },
			xaxis: { lines: { show: false } },
		},
		xaxis: {
			categories: labels,
			axisBorder: { show: false },
			axisTicks: { show: false },
			labels: {
				rotate: granularity === "day" ? -45 : 0,
				rotateAlways: granularity === "day",
				hideOverlappingLabels: true,
				style: { fontSize: "11px", fontWeight: 600, colors: "#b5b5c3" },
			},
			crosshairs: {
				show: true,
				fill: { type: "solid", color: "rgba(36, 40, 79, 0.06)" },
			},
		},
		yaxis: {
			min: 0,
			max: peak > 0 ? undefined : 10,
			labels: {
				formatter: (v) => `${Number(v).toFixed(0)} €`,
				style: { fontSize: "11px", fontWeight: 600, colors: "#b5b5c3" },
			},
		},
		tooltip: {
			theme: "light",
			y: {
				formatter: (val) => formatEurFromCents(Math.round(Number(val || 0) * 100)),
			},
			style: { fontSize: "12px" },
			marker: { show: true },
		},
		legend: { show: false },
		states: {
			hover: { filter: { type: "darken", value: 0.88 } },
			active: { filter: { type: "none" } },
		},
	};
}

export function RevenueChartPanel() {
	const chartMountRef = useRef(null);
	const apexRef = useRef(null);
	const [granularity, setGranularity] = useState("day");
	const [loading, setLoading] = useState(true);
	const [series, setSeries] = useState(null);
	const [error, setError] = useState("");

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError("");

		getPlanLogRevenueChart(granularity)
			.then((res) => {
				if (cancelled) return;
				if (res.status === 200 && res.data) {
					setSeries(res.data);
				} else {
					setSeries(null);
					setError("No se pudieron cargar los datos del gráfico.");
				}
			})
			.catch(() => {
				if (cancelled) return;
				setSeries(null);
				setError("Error al cargar ingresos.");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [granularity]);

	const chartData = useMemo(() => {
		const points = Array.isArray(series?.points) ? series.points : [];
		return {
			labels: points.map((p) => p.label),
			cents: points.map((p) => p.totalCents),
			totalCents: Number(series?.totalCents) || 0,
		};
	}, [series]);

	useEffect(() => {
		if (!chartMountRef.current || !series) return undefined;

		const options = buildChartOptions({
			labels: chartData.labels,
			seriesCents: chartData.cents,
			granularity,
		});

		if (!apexRef.current) {
			apexRef.current = new ApexCharts(chartMountRef.current, options);
			apexRef.current.render();
		} else {
			apexRef.current.updateOptions(options, false, true);
		}

		return undefined;
	}, [series, chartData, granularity]);

	useEffect(() => {
		return () => {
			if (apexRef.current) {
				apexRef.current.destroy();
				apexRef.current = null;
			}
		};
	}, []);

	const activeFilter = FILTERS.find((f) => f.id === granularity) || FILTERS[0];
	const hasData = chartData.cents.some((c) => Number(c) > 0);

	return (
		<div className="col-12 my-2">
			<div className="card revenue-chart-card">
				<div className="card-body">
					<div className="d-flex flex-wrap justify-content-between align-items-start" style={{ gap: 12 }}>
						<div>
							<h5 className="card-title mb-1">Evolución de ingresos</h5>
							<p className="text-muted small mb-0">
								{activeFilter.hint} · cobros y renovaciones registrados
							</p>
						</div>
						<div className="text-right">
							<div className="text-muted small mb-1">Total del periodo</div>
							<div className="revenue-chart-total">{loading ? "—" : formatEurFromCents(chartData.totalCents)}</div>
						</div>
					</div>

					<div className="btn-group btn-group-sm revenue-chart-filters mt-3 mb-3" role="group" aria-label="Filtro de periodo">
						{FILTERS.map((f) => (
							<button
								key={f.id}
								type="button"
								className={`btn btn-light${f.id === granularity ? " active" : ""}`}
								onClick={() => {
									if (f.id !== granularity) setGranularity(f.id);
								}}
							>
								{f.label}
							</button>
						))}
					</div>

					<div className="revenue-chart-mount-wrap">
						<div ref={chartMountRef} className="revenue-chart-mount" />

						{loading && (
							<div className="revenue-chart-overlay">
								<div className="spinner spinner-primary spinner-lg mr-3" />
								<span className="text-muted font-weight-bold">Cargando gráfico…</span>
							</div>
						)}

						{!loading && error && (
							<div className="revenue-chart-overlay revenue-chart-overlay--message">
								<span className="text-danger font-weight-bold">{error}</span>
							</div>
						)}

						{!loading && !error && !hasData && (
							<div className="revenue-chart-overlay revenue-chart-overlay--message">
								<span className="text-muted font-weight-bold">Sin ingresos registrados en este periodo</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default RevenueChartPanel;
